// @mui
/*eslint-disable */
import { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Link, Card, Grid, Typography, CardContent, FormControlLabel, Switch, Checkbox, Avatar, IconButton, Chip, Tooltip } from '@mui/material';
import { Search as SearchIcon, PersonAdd as PersonAddIcon, School as SchoolIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';

import { Marker } from 'react-mark.js';
import { months } from '../../../_mock';

// utils
import { Rankings } from '../../../components/reviews';

import { Menu } from '../../../components/dropdownMenu';

//

// ----------------------------------------------------------------------

const StyledCardMedia = styled('div')({
  position: 'relative',
  paddingTop: 'calc(100% * 3 / 4)',
});

const days = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];

const StyledTitle = styled(Link)({
  height: 44,
  overflow: 'hidden',
  WebkitLineClamp: 2,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
});

const StyledCover = styled('img')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
});

const StyledPlaceholder = styled('img')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
  filter: 'blur(5px)',
  transform: 'scale(1.1)',
  transition: 'opacity 0.3s ease-in-out',
});

// ----------------------------------------------------------------------

export default function FindEdifierCard({
  data,
  setOpen,
  setItemToDeleteId,
  setEdifierToEditData,
  setEditEdifierOpen,
  manageCredentials,
  setManageCredentials,
  setUserToEdit,
  userRole,
  searchedValue,
  isInviteCard,
  setMeetingInvitePayload,
  isMeetingStatusModifying,
  meetingInvitePayload,
  setDisplayUserDetails,
  setCurrentUser,
  manageAvailability,
  setAvailabilityPayload,
  isChangingAvailability,
  availabilityPayload,
  setReviewsGiven,
  reviewsGiven,
  pairedUsers,
  rank,
  setEdifierRankings,
  edifierRankings,
  setRankedEdifierPayload,
  isSendingPairRequest,
  rankedEdifierPayload,
  notPaired,
  setIsView,
  setChangeImage,
  setIdToChangeImage,
  // Multi-selection props
  isMultiSelectMode,
  isSelected,
  onToggleSelection,
  // Principal card flag
  isPrincipalCard = false,
}) {
  const { fname, lname, _id, title, email, role, totalRoles } = data;


  const [userAvailability, setUserAvailability] = useState(data?.isAvailable);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showHighQuality, setShowHighQuality] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const cardRef = useRef(null);

  const navigate = useNavigate();

  const inviteTime = new Date(data?.scheduleTime);

  // Image URLs for progressive loading
  const highQualityImage = data?.image
    ? `${data.image}?${Date.now()}&w=400&h=300&fit=crop&auto=format&q=80`
    : 'https://i0.wp.com/rssoeroto.ngawikab.go.id/wp-content/uploads/2022/07/user-dummy-removebg.png?ssl=1';

  const lowQualityImage = data?.image
    ? `${data.image}?${Date.now()}&w=40&h=30&fit=crop&auto=format&q=10&blur=2`
    : 'https://i0.wp.com/rssoeroto.ngawikab.go.id/wp-content/uploads/2022/07/user-dummy-removebg.png?ssl=1';

  // Calculate stats
  const fullName = `${fname} ${lname}`.trim() || email?.split('@')[0] || 'User';
  const bio = data?.info?.contentSpeciality || data?.info?.bio || '';
  const joinDate = data?.createdAt 
    ? new Date(data.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : 'Apr 2020';

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle low quality image load - then start loading high quality
  const handleLowQualityLoad = () => {
    setImageLoaded(true);
    // Start loading high quality image after low quality is shown
    setTimeout(() => {
      const img = new Image();
      img.onload = () => setShowHighQuality(true);
      img.onerror = () => setImageError(true);
      img.src = highQualityImage;
    }, 100); // Small delay to ensure smooth transition
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const isUserRanked = () => {
    return rank && JSON.stringify(edifierRankings).includes(_id);
  };

  const structureRankedEdifierPayload = () => {
    Object.keys(edifierRankings).map((keys) => {
      if (edifierRankings[keys] === _id) {
        setRankedEdifierPayload({ id: _id, rank: parseInt(keys) });
      }
    });
    discardEdifierRanking();

    // structureRankedEdifierPayload
  };

  const discardEdifierRanking = () => {
    const restructuredRankings = {};
    Object.keys(edifierRankings).map((keys) => {
      if (edifierRankings[keys] !== _id) {
        restructuredRankings = { ...restructuredRankings, keys: edifierRankings[keys] };
      }
    });

    setEdifierRankings({ ...restructuredRankings });
  };
  const returnRoute = () => {
    if (pairedUsers) {
      return `/dashboard/user-profile?id=${_id}&role=${userRole}&pairedUsers=1`;
    }
    return `/dashboard/user-profile?id=${_id}&role=${userRole}`;
  };

  // Helper function to get role configuration with colors
  const getRoleConfig = (roleType) => {
    const roleMap = {
      'COACH': { label: 'Edifier', backgroundColor: '#dbeafe', textColor: '#1e40af' }, // Light pastel blue background, dark blue text
      'TEACHER': { label: 'Teacher', backgroundColor: '#d1fae5', textColor: '#065f46' }, // Light pastel green background, dark green text
      'SITE-LEADER': { label: 'Site-Leader', backgroundColor: '#fed7aa', textColor: '#9a3412' }, // Light pastel orange background, dark orange text
      'PRINCIPAL': { label: 'Principal', backgroundColor: '#e9d5ff', textColor: '#6b21a8' }, // Light pastel purple background, dark purple text
      'ADMIN': { label: 'Admin', backgroundColor: '#fecaca', textColor: '#991b1b' }, // Light pastel red background, dark red text
    };
    return roleMap[roleType] || { label: roleType || 'User', backgroundColor: '#f3f4f6', textColor: '#374151' }; // Light grey background, dark grey text for unknown
  };

  return (
    <Grid item xs={12} sm={6} lg={4} xl={3}>
      <Card
        sx={{
          position: 'relative',
          borderRadius: 2,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          bgcolor: 'background.paper',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: isSelected ? '3px solid' : '3px solid transparent',
          borderColor: isSelected ? 'primary.main' : 'transparent',
          transition: 'border-color 0.2s ease-in-out',
          pb:
            userAvailability !== data?.isAvailable
              ? { xs: '80px', md: '50px' }
              : isUserRanked()
                ? { xs: '80px', md: '50px' }
                : 0,
        }}
      >
        {/* Action Icons - Top Right */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            display: 'flex',
            gap: 1,
            zIndex: 10,
          }}
        >
          {(userRole === 'admin' || manageCredentials || userRole === 'site-leader' || userRole === 'principal') && (
            <Menu
              setIsView={setIsView}
              setMenuOpen={setOpen}
              setItemToDeleteId={setItemToDeleteId}
              id={_id}
              data={data}
              setEdifierToEditData={setEdifierToEditData}
              setEditEdifierOpen={setEditEdifierOpen}
              manageCredentials={manageCredentials}
              setManageCredentials={setManageCredentials}
              setUserToEdit={setUserToEdit}
              setChangeImage={setChangeImage}
              setIdToChangeImage={setIdToChangeImage}
              isViewOnly={userRole === 'principal'}
              isPrincipalCard={isPrincipalCard}
            />
          )}
        </Box>

        {/* Selection checkbox - only show in multi-select mode */}
        {isMultiSelectMode && (
          <Box sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 10,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            p: 0.5
          }}>
            <Checkbox
              checked={isSelected}
              onChange={onToggleSelection}
              color="primary"
              sx={{
                p: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: 24
                }
              }}
            />
          </Box>
        )}

        <CardContent sx={{ p: 3, position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* Profile Picture and Role Chip */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 2,
              mb: 2,
            }}
          >
            <Avatar
              src={data?.image ? `${data.image}?${Date.now()}` : undefined}
              alt={fullName}
              sx={{
                width: 80,
                height: 80,
                border: '3px solid',
                borderColor: 'background.paper',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                flexShrink: 0,
              }}
            >
              {!data?.image && fullName.charAt(0).toUpperCase()}
            </Avatar>

            {/* Role Chip */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pb: 0.5 }}>
              {userRole?.toLowerCase() === 'admin' && totalRoles && totalRoles.length > 0 ? (
                totalRoles.map((roleItem, index) => {
                  const roleConfig = getRoleConfig(roleItem);
                  return (
                    <Chip
                      key={index}
                      label={roleConfig.label}
                      size="small"
                      sx={{
                        backgroundColor: roleConfig.backgroundColor,
                        color: roleConfig.textColor,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: '24px',
                        borderRadius: 1,
                      }}
                    />
                  );
                })
              ) : (
                (() => {
                  const displayRole = pairedUsers 
                    ? (userRole.toLowerCase() === 'teacher' ? 'COACH' : 'TEACHER')
                    : role;
                  const roleConfig = getRoleConfig(displayRole);
                  return (
                    <Chip
                      label={roleConfig.label}
                      size="small"
                      sx={{
                        backgroundColor: roleConfig.backgroundColor,
                        color: roleConfig.textColor,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: '24px',
                        borderRadius: 1,
                      }}
                    />
                  );
                })()
              )}
            </Box>
          </Box>

          {/* Name */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              fontSize: '1.5rem',
              color: 'text.primary',
              mb: 1,
              textTransform: 'capitalize',
            }}
          >
            <Marker mark={searchedValue}>{fullName}</Marker>
          </Typography>

          {/* Email */}
          <Box sx={{ display: 'flex', columnGap: '4.48px', alignItems: 'center', mb: 2 }}>
            <Box component="img" src="/assets/icons/emailIcon.svg" sx={{ width: '16px', height: '16px' }} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {email}
            </Typography>
          </Box>

          {/* Statistics Footer - Sticks to bottom */}
          {!isInviteCard && !manageCredentials &&  <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              pt: 2,
              pb: 2,
              px: 2,
              mx: -3,
              mb: -3,
              mt: 'auto',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Joined Date - Left */}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              Joined <strong>{joinDate}</strong>
            </Typography>

            {/* Partnership site logo - Right (opposite Joined Date) */}
            {['admin', 'site-leader', 'principal'].includes(userRole?.toLowerCase?.()) &&
              data?.partnershipSite &&
              (data.partnershipSite.image || data.partnershipSite.name) && (
                <Tooltip title={data.partnershipSite.name || 'Partnership site'} placement="top" arrow>
                  <Avatar
                    src={
                      data.partnershipSite.image
                        ? `${data.partnershipSite.image}?${Date.now()}`
                        : undefined
                    }
                    alt={data.partnershipSite.name || ''}
                    variant="rounded"
                    sx={{
                      ml: 'auto',
                      width: 28,
                      height: 28,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'primary.lighter',
                      '& .MuiAvatar-img': { objectFit: 'cover' },
                    }}
                  >
                    {!data.partnershipSite.image &&
                      (data.partnershipSite.name || '')
                        .trim()
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((part) => part.charAt(0).toUpperCase())
                        .join('') || 'PS'}
                  </Avatar>
                </Tooltip>
              )}

            {/* View Profile Link - Right */}
             {!['admin', 'site-leader', 'principal'].includes(userRole) && <Link
              onClick={() => navigate(returnRoute())}
              sx={{
                color: (theme) => theme.palette.primary.main,
                fontSize: '0.75rem',
                fontWeight: 500,
                ml: 'auto',
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              View Profile
            </Link>}
          </Box>}

          {/* Additional Info Section */}
          <Box sx={{ mt: 2 }}>

            {/* Invite Time */}
            {isInviteCard && (
              <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#000' }}>
                {`${inviteTime.getDate()} ${months[inviteTime.getMonth()]} ${days[inviteTime.getDay()]}  ${inviteTime
                  .toLocaleTimeString()
                  .replace(/([\d]+:[\d]{2})(:[\d]{2})(.*)/, '$1$3')}`}
              </Typography>
            )}

            {/* Rankings for teachers */}
            {userRole === 'teacher' && rank && notPaired && (
              <Box sx={{ mt: 2 }}>
                <Rankings
                  id={_id}
                  setReviewsGiven={setReviewsGiven}
                  reviewsGiven={reviewsGiven}
                  setEdifierRankings={setEdifierRankings}
                  edifierRankings={edifierRankings}
                />
              </Box>
            )}

            {/* Availability Switch */}
            {manageAvailability && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={userAvailability}
                      onChange={(e) => setUserAvailability(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Available"
                  sx={{
                    '& .MuiTypography-root': { color: 'black', fontSize: '0.875rem' },
                  }}
                />
              </Box>
            )}
          </Box>
        </CardContent>

        {/* Old Image Section - Removed but keeping for reference */}
        {false && (
        <StyledCardMedia sx={{ minHeight: '350px', p: '0' }} ref={cardRef}>
          {/* Selection checkbox - only show in multi-select mode */}
          {isMultiSelectMode && (
            <Box sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              p: 0.5
            }}>
              <Checkbox
                checked={isSelected}
                onChange={onToggleSelection}
                color="primary"
                sx={{
                  p: 0,
                  '& .MuiSvgIcon-root': {
                    fontSize: 24
                  }
                }}
              />
            </Box>
          )}

          {/* Only load images when card is in view */}
          {isInView ? (
            <>
              {/* Low quality placeholder image - loads first */}
              <StyledPlaceholder
                alt={title}
                sx={{
                  minHeight: '250px',
                  opacity: showHighQuality ? 0 : 1,
                  transition: 'opacity 0.3s ease-in-out',
                }}
                src={lowQualityImage}
                onLoad={handleLowQualityLoad}
                onError={handleImageError}
              />

              {/* High quality image - only renders when ready */}
              {showHighQuality && (
                <StyledCover
                  alt={title}
                  sx={{
                    minHeight: '250px',
                    opacity: 1,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                  src={highQualityImage}
                />
              )}
            </>
          ) : (
            /* Placeholder while not in view */
            <Box
              sx={{
                minHeight: '250px',
                width: '100%',
                height: '100%',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          )}
        </StyledCardMedia>
        )}

        {isInviteCard && (
          <Box sx={{ display: 'flex', columnGap: '10px', p: '10px', pb: '20px' }}>
            <LoadingButton
              variant="contained"
              color="success"
              onClick={() => setMeetingInvitePayload({ inviteId: data?._id, status: 'ACCEPTED' })}
              loading={meetingInvitePayload?.inviteId === data?._id && isMeetingStatusModifying}
            >
              Accept Invite
            </LoadingButton>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={() => setMeetingInvitePayload({ inviteId: data?._id, status: 'REJECTED' })}
              loading={meetingInvitePayload?.inviteId === data?._id && isMeetingStatusModifying}
            >
              Reject Invite
            </LoadingButton>
          </Box>
        )}

        {manageAvailability && userAvailability !== data?.isAvailable && (
          <Box
            sx={{
              display: 'flex',
              columnGap: '20px',
              position: 'absolute',
              bottom: '20px',
              left: '20px',
              flexWrap: 'wrap',
              justifyContent: 'center',
              rowGap: '10px',
              pr: '17px',
            }}
          >
            <LoadingButton
              variant="contained"
              loading={isChangingAvailability && data?._id === availabilityPayload._id}
              // color="white"
              disabled={isChangingAvailability && data?._id !== availabilityPayload._id}
              onClick={() => setUserAvailability(data?.isAvailable)}
              sx={{ width: '118px' }}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              variant="contained"
              // loading={isModifyingTimeSlots}
              loading={isChangingAvailability && data?._id === availabilityPayload._id}
              disabled={isChangingAvailability && data?._id !== availabilityPayload._id}
              color="main"
              onClick={(e) => setAvailabilityPayload({ _id: data?._id, isAvailable: userAvailability })}
              sx={{ width: '118px' }}
            >
              Save
            </LoadingButton>
          </Box>
        )}

        {isUserRanked() && (
          <Box sx={{ display: 'flex', columnGap: '20px', position: 'absolute', bottom: '20px', left: '20px' }}>
            <LoadingButton
              variant="contained"
              loading={isSendingPairRequest && JSON.stringify(rankedEdifierPayload).includes(_id)}
              disabled={isSendingPairRequest && JSON.stringify(rankedEdifierPayload).includes(_id) === false}
              onClick={() => discardEdifierRanking()}
              sx={{ width: '118px' }}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              variant="contained"
              loading={isSendingPairRequest && JSON.stringify(rankedEdifierPayload).includes(_id)}
              disabled={isSendingPairRequest && JSON.stringify(rankedEdifierPayload).includes(_id) === false}
              color="main"
              onClick={(e) => structureRankedEdifierPayload()}
              sx={{ width: '118px' }}
            >
              Save
            </LoadingButton>
          </Box>
        )}
      </Card>
    </Grid>
  );
}
